// The pairing seam (F-STN-02, #80).
//
// "Adding a PC" is two steps: define the station on the Stations screen (its number, class and zone —
// F-ORG-03 #408), then pair the physical PC to it. The PC runs the pairing handshake itself
// (`POST /v1/device-pairings`, spec/10 §3.2) and shows an **8-character code** full-screen. A staff
// member with `stations:manage` reads that code off the PC and approves it here, which binds the PC's
// device key to the station (`POST /v1/organizations/{o}/device-pairings/{code}/approve`, spec/07 §5).
//
// This module is the console's pure half of that flow: whether a station already has a device bound,
// and normalising the code a staff member types so it matches what the PC shows. The approve call
// itself is the `useApproveDevicePairing` mutation (`queries.ts`).

import type { Station } from '@clavian/api-client';

/** The pairing code the PC shows is exactly this many characters (spec/10 §3.2). */
export const PAIRING_CODE_LENGTH = 8;

/** Whether a station has a device bound to it, from its DTO (`deviceId` set once it is paired). */
export type StationPairing =
  | { readonly status: 'paired'; readonly deviceId: string }
  | { readonly status: 'unpaired' };

/** A station's pairing state: paired once the api has bound a device to it (spec/10 §3.2 rule 4). */
export function pairingOf(station: Station): StationPairing {
  return station.deviceId === null ? { status: 'unpaired' } : { status: 'paired', deviceId: station.deviceId };
}

/**
 * Normalise a code a staff member typed to what the PC shows: upper case, letters and digits only (no
 * spaces or dashes they may add for readability), and never longer than a code. Idempotent, so it can
 * clean every keystroke.
 */
export function normalizePairingCode(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, PAIRING_CODE_LENGTH);
}

/** Whether a normalised code is the full length the PC shows, so it is worth sending. */
export function isCompletePairingCode(code: string): boolean {
  return code.length === PAIRING_CODE_LENGTH;
}
